/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   CORE / MAIL-RULES-DATA — custom_mail_rules CRUD (S226 CUT C)
   =============================================================================
   Datenschicht für die eigenen Mail-Regeln (Builder). Zugriff über den
   RLS-gescopeten authenticated-Client (window.App.supabase); Server erzwingt
   Tenant + Rolle (owner/admin/manager) via Policies (phase_166). Modul-Cache
   `_rules` speist die Vorlagen-Übersicht synchron; Writes patchen den Cache +
   melden Änderungen an die registrierte Re-Render-Callback.

   KEINE Business-Logik im Client — Trigger-Katalog/Consent-Gate/Idempotenz
   sitzen in der DB (mail-triggers.js Single-Source + gg_enqueue_custom_mails).
   Hier nur Persistenz + Cache.
============================================================================ */

const TABLE = 'custom_mail_rules';

// Modul-Cache (überlebt Re-Render, nicht Reload). Loaded-Flag verhindert
// Dauer-Refetch bei jedem Settings-Render.
let _rules = [];
let _loaded = false;
let _loadInFlight = null;
let _onChange = null;

function supa() {
  return (typeof window !== 'undefined' && window.App && window.App.supabase) || null;
}

/**
 * S226-HF2 (#3) — Tenant-ID für den NOT-NULL/RLS-CHECK beim INSERT AUSSCHLIESSLICH
 * über den ZENTRALEN Resolver (core/state.js currentRestaurantId →
 * window.App.getCurrentRestaurantId). Der frühere lokale Resolver las
 * state.restaurant.id / state.settings.restaurantId — BEIDE existieren im echten
 * State nicht → restaurant_id=null → RLS-403 beim Builder-Anlegen (Redundanz-
 * Verstoß, gg-fakten-statt-raten). Kein Dual-System mehr.
 */
export function currentRestaurantId() {
  if (typeof window !== 'undefined' && window.App
      && typeof window.App.getCurrentRestaurantId === 'function') {
    return window.App.getCurrentRestaurantId() || null;
  }
  return null;
}

/** Callback registrieren, die nach jedem Cache-Write feuert (Re-Render-Hook). */
export function onCustomRulesChanged(cb) {
  _onChange = typeof cb === 'function' ? cb : null;
}
function _notify() { try { if (_onChange) _onChange(); } catch (_) { /* tolerant */ } }

export function getCustomMailRulesCache() { return _rules.slice(); }
export function isCustomRulesLoaded() { return _loaded; }
export function getCustomMailRule(id) { return _rules.find(r => r.id === id) || null; }
export function _resetForTests() { _rules = []; _loaded = false; _loadInFlight = null; _onChange = null; }

/** Lädt die Regeln des Tenants (RLS-gescopet). Dedupliziert parallele Aufrufe. */
export async function loadCustomMailRules(force) {
  if (_loaded && !force) return _rules.slice();
  if (_loadInFlight) return _loadInFlight;
  const client = supa();
  if (!client) { _loaded = true; return []; }
  _loadInFlight = (async () => {
    try {
      const { data, error } = await client.from(TABLE)
        .select('*').order('created_at', { ascending: true });
      if (error) throw error;
      _rules = Array.isArray(data) ? data : [];
      _loaded = true;
      return _rules.slice();
    } catch (e) {
      console.warn('[loadCustomMailRules]', e && e.message);
      return _rules.slice();
    } finally {
      _loadInFlight = null;
    }
  })();
  return _loadInFlight;
}

/**
 * Neue Regel anlegen (create-on-accept-only: erst beim finalen Speichern).
 * @param {{name,trigger_event,offset_minutes,recipient_mode,fixed_email,subject,body,attachment,enabled}} r
 * @returns {Promise<object|null>} die angelegte Zeile oder null bei Fehler
 */
export async function createCustomMailRule(r) {
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client');
  const row = {
    restaurant_id:  currentRestaurantId(),
    name:           r.name,
    enabled:        r.enabled !== false,
    trigger_event:  r.trigger_event,
    offset_minutes: Number.isFinite(r.offset_minutes) ? r.offset_minutes : 0,
    recipient_mode: r.recipient_mode,
    fixed_email:    r.recipient_mode === 'fixed' ? (r.fixed_email || null) : null,
    subject:        r.subject,
    body:           r.body,
    attachment:     r.attachment || null,
  };
  const { data, error } = await client.from(TABLE).insert(row).select().single();
  if (error) throw error;
  _rules.push(data);
  _notify();
  return data;
}

/** Bestehende Regel patchen (Builder-Edit). */
export async function updateCustomMailRule(id, patch) {
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client');
  const clean = { ...patch };
  if ('recipient_mode' in clean && clean.recipient_mode !== 'fixed') clean.fixed_email = null;
  const { data, error } = await client.from(TABLE).update(clean).eq('id', id).select().single();
  if (error) throw error;
  const i = _rules.findIndex(x => x.id === id);
  if (i >= 0) _rules[i] = data; else _rules.push(data);
  _notify();
  return data;
}

/** Regel löschen. */
export async function deleteCustomMailRule(id) {
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client');
  const { error } = await client.from(TABLE).delete().eq('id', id);
  if (error) throw error;
  _rules = _rules.filter(x => x.id !== id);
  _notify();
}

/** enabled-Toggle (eigene Mails haben ihr eigenes enabled wie die Kind-Toggles). */
export async function setCustomMailRuleEnabled(id, enabled) {
  return updateCustomMailRule(id, { enabled: !!enabled });
}
